// Mobile-specific authentication handler for Teams SSO
import * as microsoftTeams from "@microsoft/teams-js";

class MobileAuthHandler {
  constructor() {
    this.ssoToken = null;
    this.tokenExpiry = null;
    this.isRefreshing = false;
  }

  // Store the token received during authentication
  storeAuthToken(token) {
    console.log('📱 Storing auth token for mobile');
    this.ssoToken = token;
    this.tokenExpiry = Date.now() + (50 * 60 * 1000); // 50 minutes
    localStorage.setItem('mobile_auth_token', token);
    localStorage.setItem('mobile_auth_token_expiry', this.tokenExpiry.toString());
  }

  // Get SSO token from Teams SDK (mobile)
  async getSSOToken() {
    // Check if we have a valid cached token in memory
    if (this.ssoToken && this.tokenExpiry && this.tokenExpiry > Date.now()) {
      console.log('📱 Using cached SSO token from memory');
      return this.ssoToken;
    }

    // Check localStorage for stored token
    const storedToken = localStorage.getItem('mobile_auth_token');
    const storedExpiry = localStorage.getItem('mobile_auth_token_expiry');
    if (storedToken && storedExpiry && parseInt(storedExpiry) > Date.now()) {
      console.log('📱 Using stored SSO token from localStorage');
      this.ssoToken = storedToken;
      this.tokenExpiry = parseInt(storedExpiry);
      return storedToken;
    }

    // Prevent concurrent token requests
    if (this.isRefreshing) {
      console.log('⏳ Token refresh already in progress, waiting...');
      await new Promise(resolve => setTimeout(resolve, 500));
      return this.getSSOToken();
    }

    this.isRefreshing = true;
    
    try {
      console.log('📱 Getting fresh SSO token from Teams SDK...');
      
      // Get SSO token from Teams SDK
      const token = await new Promise((resolve, reject) => {
        microsoftTeams.authentication.getAuthToken({
          successCallback: (token) => {
            console.log('✅ SSO token obtained from Teams SDK');
            resolve(token);
          },
          failureCallback: (error) => {
            console.error('❌ Failed to get SSO token:', error);
            reject(error);
          }
        });
      });

      // Cache the token (SSO tokens typically expire in 1 hour)
      this.ssoToken = token;
      this.tokenExpiry = Date.now() + (50 * 60 * 1000); // 50 minutes
      this.isRefreshing = false;
      
      return token;
    } catch (error) {
      this.isRefreshing = false;
      console.error('❌ Failed to get SSO token:', error);
      
      // Try to get from localStorage as fallback
      const storedAuth = localStorage.getItem('mobile_auth_token');
      if (storedAuth) {
        console.log('📱 Using stored auth token as fallback');
        return storedAuth;
      }
      
      throw error;
    }
  }

  // Clear cached token
  clearToken() {
    this.ssoToken = null;
    this.tokenExpiry = null;
    this.isRefreshing = false;
  }

  // Create a credential-like object for compatibility with http.js
  createCredential() {
    return {
      getToken: async (scopes) => {
        console.log('📱 Mobile credential getToken called with scopes:', scopes);
        const token = await this.getSSOToken();
        return { token }; // Return in the format expected by http.js
      }
    };
  }
}

// Singleton instance
const mobileAuthHandler = new MobileAuthHandler();

export default mobileAuthHandler;